import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Instagram, Plane } from "lucide-react";
import { Logo } from "./Logo";
import { waLink, INSTAGRAM_URL } from "./constants";
import { scrollToSection } from "./Reveal";

const LINKS = [
  { label: "Aviation", hash: "#aviation" },
  { label: "Services", hash: "#services" },
  { label: "Calculator", hash: "#calculator" },
  { label: "About", hash: "#about" },
  { label: "Process", hash: "#process" },
  { label: "Stories", hash: "#testimonials" },
];

const AVIATION_WA = waLink(
  "Hi, I work in aviation and was wondering if I could book an appointment?"
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(hash);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        scrolled
          ? "bg-[#03222D]/90 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.hash}
              href={l.hash}
              onClick={go(l.hash)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-slate-300 hover:text-amber-400 transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-link"
            aria-label="KW Mortgages on Instagram"
            className="p-2.5 rounded-full border border-white/10 text-slate-300 hover:text-amber-400 hover:border-amber-400/40 transition-colors duration-200"
          >
            <Instagram size={17} />
          </a>
          <a
            href={AVIATION_WA}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-aviation-button"
            className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-semibold text-[#03222D] transition-colors duration-200 hover:bg-amber-300"
          >
            <Plane size={16} />
            Aviation Mortgages
          </a>
          <a
            href={waLink("Hi Kieran, I'd like to chat about a mortgage.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-[#03222D] hover:bg-[#2be072] transition-colors duration-200"
          >
            <MessageCircle size={16} />
            WhatsApp Kieran
          </a>
        </div>
        <button
          className="lg:hidden p-2 text-slate-200"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#03222D]/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.hash}
                  href={l.hash}
                  onClick={go(l.hash)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="text-base text-slate-200 hover:text-amber-400 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={AVIATION_WA}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-aviation-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-[#03222D]"
              >
                <Plane size={16} />
                Aviation Mortgages
              </a>
              <a
                href={waLink("Hi Kieran, I'd like to chat about a mortgage.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-[#03222D]"
              >
                <MessageCircle size={16} />
                WhatsApp Kieran
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
